'use client';

import { use, useMemo } from 'react';
import {
    getSchoolData,
    getSchoolName,
    SchoolUnit,
    Account,
    BankAccount,
    Deposit,
    Transfer
} from '@/lib/data';
import {
    Wallet,
    TrendingUp,
    TrendingDown,
    ArrowRightLeft,
    Banknote,
    FileStack,
    HandCoins,
    Receipt,
    CreditCard,
    Plus,
    Search,
    Filter,
    ArrowRight,
    ChevronRight,
    ArrowLeft,
    Building2,
    CheckCircle2,
    AlertCircle,
    Calendar,
    Download
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';

export default function FinanceDashboardPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit } = use(params);
    const validUnits: SchoolUnit[] = ['paud', 'paud-pekanbaru', 'paud-kampar', 'paud-siak', 'sd', 'smp', 'smk'];

    if (!validUnits.includes(unit as SchoolUnit)) {
        notFound();
    }

    const schoolUnit = unit as SchoolUnit;
    const schoolData = getSchoolData(schoolUnit);
    const schoolName = getSchoolName(schoolUnit);

    // Extracting finance-specific data
    const accounts = ((schoolData as any)[`${unit.split('-')[0]}Accounts`] || []) as Account[];
    const bankAccounts = ((schoolData as any)[`${unit.split('-')[0]}BankAccounts`] || []) as BankAccount[];
    const deposits = ((schoolData as any)[`${unit.split('-')[0]}Deposits`] || []) as Deposit[];
    const transfers = ((schoolData as any)[`${unit.split('-')[0]}Transfers`] || []) as Transfer[];

    const financeStats = useMemo(() => {
        return [
            { label: 'Total Saldo Kas', value: accounts.find(a => a.name === 'Kas')?.balance || 'Rp 0', icon: Wallet, color: 'text-primary', bg: 'bg-primary-soft' },
            { label: 'Saldo Bank Utama', value: bankAccounts[0]?.balance || 'Rp 0', icon: Banknote, color: 'text-info', bg: 'bg-info-soft' },
            { label: 'Total Setoran (Feb)', value: 'Rp 25.500.000', icon: TrendingUp, color: 'text-success', bg: 'bg-success-soft' },
            { label: 'Inter-unit Transfer', value: 'Rp 3.800.000', icon: ArrowRightLeft, color: 'text-warning', bg: 'bg-warning-soft' },
        ];
    }, [accounts, bankAccounts]);

    const quickActions = [
        { name: 'Data List Akun', desc: 'Kelola Chart of Accounts (COA)', icon: FileStack, href: `/yayasan/${unit}/keuangan/akun`, color: 'from-blue-500/10 to-blue-600/10', iconColor: 'text-blue-600' },
        { name: 'Rekening Bank', desc: 'Monitoring saldo & mutasi bank', icon: Banknote, href: `/yayasan/${unit}/keuangan/rekening`, color: 'from-indigo-500/10 to-indigo-600/10', iconColor: 'text-indigo-600' },
        { name: 'Input Setoran', desc: 'Catat penerimaan kas baru', icon: HandCoins, href: `/yayasan/${unit}/keuangan/setoran`, color: 'from-emerald-500/10 to-emerald-600/10', iconColor: 'text-emerald-600' },
        { name: 'Transfer Kas', desc: 'Kirim dana antar unit sekolah', icon: ArrowRightLeft, href: `/yayasan/${unit}/keuangan/transfer`, color: 'from-amber-500/10 to-amber-600/10', iconColor: 'text-amber-600' },
        { name: 'Biaya & Pengeluaran', desc: 'Kelola operasional & cas laci', icon: Receipt, href: `/yayasan/${unit}/keuangan/pengeluaran`, color: 'from-rose-500/10 to-rose-600/10', iconColor: 'text-rose-600' },
    ];

    return (
        <main className="p-4 md:p-10 space-y-10 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Professional Header Section */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="relative z-10 flex flex-col gap-8">
                    <Link
                        href={`/yayasan/${unit}`}
                        className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest hover:text-primary transition-all group/back"
                    >
                        <ArrowLeft className="w-3.5 h-3.5 group-hover/back:-translate-x-1 transition-transform" />
                        Kembali ke Dashboard Unit
                    </Link>

                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <Wallet className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Manajemen Keuangan</h1>
                                <div className="flex items-center gap-2">
                                    <p className="text-secondary font-medium">{schoolName}</p>
                                    <span className="w-1.5 h-1.5 rounded-full bg-border-main"></span>
                                    <p className="text-xs font-bold text-muted uppercase tracking-widest">KSO-FINANCE v2.0</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <button className="px-6 py-3.5 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-white hover:border-primary transition-all shadow-sm flex items-center gap-2 uppercase tracking-widest">
                                <Download className="w-4 h-4 text-muted" /> Download Rekap
                            </button>
                            <button className="px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-bold hover:shadow-xl hover:shadow-primary/25 transition-all flex items-center gap-2 uppercase tracking-widest">
                                <Plus className="w-4 h-4" /> Transaksi Baru
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Financial Stats Grid */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {financeStats.map((stat, idx) => (
                    <div key={idx} className="bg-surface p-6 rounded-4xl border border-border-main shadow-sm group hover:shadow-md transition-all">
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 ${stat.bg} ${stat.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                <stat.icon className="w-6 h-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest truncate">{stat.label}</p>
                                <p className="text-xl font-bold text-foreground truncate">{stat.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                {/* Left Side: Navigation & Quick Actions */}
                <div className="lg:col-span-2 space-y-10">
                    <section className="space-y-6">
                        <div className="flex items-center justify-between px-2">
                            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                                <FileStack className="w-5 h-5 text-primary" /> Modul Keuangan
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {quickActions.map((action, idx) => (
                                <Link
                                    key={idx}
                                    href={action.href}
                                    className={`group p-6 rounded-4xl border border-border-main bg-linear-to-br ${action.color} hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden`}
                                >
                                    <div className="relative z-10 flex flex-col h-full justify-between gap-6">
                                        <div className="flex items-start justify-between">
                                            <div className={`w-12 h-12 rounded-2xl bg-surface shadow-sm border border-black/5 flex items-center justify-center ${action.iconColor}`}>
                                                <action.icon className="w-6 h-6" />
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-muted group-hover:text-primary group-hover:translate-x-1 transition-all" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-foreground text-base group-hover:text-primary transition-colors">{action.name}</h3>
                                            <p className="text-xs text-secondary font-medium mt-1">{action.desc}</p>
                                        </div>
                                    </div>
                                    <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
                                </Link>
                            ))}
                        </div>
                    </section>

                    {/* Recent Transactions List */}
                    <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden">
                        <div className="p-7 border-b border-border-main flex items-center justify-between bg-background/30">
                            <h3 className="font-bold text-lg flex items-center gap-3">
                                <Receipt className="w-5 h-5 text-primary" /> Aktivitas Terbaru
                            </h3>
                            <button className="text-[10px] font-bold text-primary uppercase tracking-widest hover:underline flex items-center gap-1.5">
                                Selengkapnya <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="divide-y divide-border-main">
                            {deposits.slice(0, 5).map((dep, idx) => (
                                <div key={idx} className="p-6 flex items-center justify-between hover:bg-primary-soft/10 transition-colors group">
                                    <div className="flex items-center gap-5">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 ${dep.type === 'masuk' ? 'bg-success-soft text-success' : 'bg-info-soft text-info'}`}>
                                            {dep.type === 'masuk' ? <TrendingUp className="w-5 h-5" /> : <Banknote className="w-5 h-5" />}
                                        </div>
                                        <div>
                                            <p className="font-bold text-[13px] text-foreground group-hover:text-primary transition-colors">{dep.description}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[10px] font-medium text-muted uppercase tracking-widest">{dep.date}</span>
                                                <span className="w-1 h-1 rounded-full bg-border-main"></span>
                                                <span className="text-[10px] font-bold text-primary uppercase tracking-widest">SETORAN</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-sm text-success tabular-nums">+{dep.amount}</p>
                                        <div className="flex items-center gap-1 justify-end mt-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
                                            <span className="text-[9px] font-bold text-muted uppercase tracking-widest">LUNAS</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                {/* Right Side: Bank Accounts & Budget Summary */}
                <div className="space-y-10">
                    <section className="bg-surface rounded-4xl border border-border-main shadow-sm p-8 relative overflow-hidden">
                        <div className="relative z-10 space-y-8">
                            <div className="flex items-center justify-between">
                                <h3 className="font-bold text-lg flex items-center gap-3">
                                    <Banknote className="w-5 h-5 text-primary" /> Rekening Aktif
                                </h3>
                                <Link href={`/yayasan/${unit}/keuangan/rekening`} className="p-2 hover:bg-primary-soft rounded-lg transition-colors">
                                    <Plus className="w-4 h-4 text-primary" />
                                </Link>
                            </div>

                            <div className="space-y-4">
                                {bankAccounts.map((acc, idx) => (
                                    <div key={idx} className="p-5 bg-background border border-border-main rounded-3xl hover:border-primary/20 transition-all group">
                                        <div className="flex items-center justify-between mb-4">
                                            <span className="px-2.5 py-1 bg-surface border border-border-main rounded-lg text-primary text-[9px] font-bold uppercase tracking-wider">{acc.bankName}</span>
                                            <span className="text-[9px] text-muted font-bold tracking-widest">{acc.type}</span>
                                        </div>
                                        <h4 className="text-base font-bold text-foreground mb-0.5 tracking-tight">{acc.balance}</h4>
                                        <p className="text-[10px] text-muted font-mono tracking-widest mb-4">{acc.accountNumber}</p>
                                        <div className="flex items-center justify-between pt-4 border-t border-border-main/50">
                                            <span className="text-[9px] font-bold text-muted uppercase tracking-wider">COA LINKED</span>
                                            <span className="text-[9px] font-bold text-primary">{acc.linkedAccount}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="bg-linear-to-br from-[#1a2e1a] to-[#0a140a] rounded-4xl p-8 text-white relative overflow-hidden shadow-2xl">
                        <div className="relative z-10 space-y-6">
                            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-md">
                                <TrendingUp className="w-6 h-6 text-primary-soft" />
                            </div>
                            <div>
                                <h4 className="text-xl font-bold">Target Efisiensi</h4>
                                <p className="text-white/60 text-xs mt-1 leading-relaxed">Pencapaian anggaran unit untuk periode semester ganjil 2024/2025.</p>
                            </div>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                                        <span>Realisasi Anggaran</span>
                                        <span>72%</span>
                                    </div>
                                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                        <div className="h-full bg-primary w-[72%] rounded-full shadow-[0_0_10px_var(--color-primary)]"></div>
                                    </div>
                                </div>
                            </div>
                            <button className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all">
                                Lihat Laporan Detail
                            </button>
                        </div>
                        <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl"></div>
                    </section>

                    {/* Quick Info Box */}
                    <div className="bg-info-soft/30 border border-info/10 rounded-4xl p-8 flex gap-5 items-start">
                        <AlertCircle className="w-6 h-6 text-info shrink-0" />
                        <div className="space-y-1">
                            <h4 className="text-sm font-bold text-info">Sinkronisasi Audit</h4>
                            <p className="text-xs text-secondary leading-relaxed">Seluruh data keuangan unit ini disinkronkan secara otomatis dengan sistem akuntansi yayasan setiap pukul 00:00 WIB.</p>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
